export const CONSENT_COOKIE_NAME = 'owlscran_consent';
export const CONSENT_VERSION = 1;
export const CONSENT_BANNER_VERSION = '1.0';
export const CONSENT_COOKIE_MAX_AGE_SECONDS = 15_552_000; // 180 days

export type ConsentChoice = 'accept_all' | 'reject_all' | 'customise';

export interface ConsentRecord {
  version: number;
  timestamp: string;
  analytics: boolean;
  session_recording: boolean;
  choice: ConsentChoice;
}

export interface ConsentPreferencesInput {
  analytics: boolean;
  sessionRecording: boolean;
}

export interface TelemetryConsentMode {
  analyticsEnabled: boolean;
  sessionRecordingEnabled: boolean;
  statsigGateOnlyMode: boolean;
}

export interface ConsentSnapshot {
  consent: ConsentRecord | null;
  shouldPrompt: boolean;
  telemetry: TelemetryConsentMode;
}

const DEFAULT_CUSTOMISE_PREFERENCES: ConsentPreferencesInput = {
  analytics: true,
  sessionRecording: false,
};

export function buildConsentRecord(
  choice: ConsentChoice,
  prefs: ConsentPreferencesInput = DEFAULT_CUSTOMISE_PREFERENCES,
): ConsentRecord {
  const now = new Date().toISOString();

  if (choice === 'accept_all') {
    return {
      version: CONSENT_VERSION,
      timestamp: now,
      analytics: true,
      session_recording: true,
      choice,
    };
  }

  if (choice === 'reject_all') {
    return {
      version: CONSENT_VERSION,
      timestamp: now,
      analytics: false,
      session_recording: false,
      choice,
    };
  }

  return {
    version: CONSENT_VERSION,
    timestamp: now,
    analytics: prefs.analytics,
    session_recording: prefs.sessionRecording,
    choice,
  };
}

export function serializeConsentCookieValue(consent: ConsentRecord): string {
  return JSON.stringify(consent);
}

export function parseConsentCookieValue(value: string | null | undefined): ConsentRecord | null {
  if (!value) return null;

  try {
    const parsed = JSON.parse(value) as Partial<ConsentRecord>;
    return isValidConsentRecord(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function getConsentSnapshot(rawCookieValue: string | null | undefined): ConsentSnapshot {
  const consent = parseConsentCookieValue(rawCookieValue);

  if (!consent || consent.version !== CONSENT_VERSION) {
    return {
      consent: null,
      shouldPrompt: true,
      telemetry: {
        analyticsEnabled: true,
        sessionRecordingEnabled: false,
        statsigGateOnlyMode: false,
      },
    };
  }

  const analyticsEnabled = consent.analytics;
  const sessionRecordingEnabled = consent.session_recording;

  return {
    consent,
    shouldPrompt: false,
    telemetry: {
      analyticsEnabled,
      sessionRecordingEnabled,
      statsigGateOnlyMode: !analyticsEnabled,
    },
  };
}

export function readConsentCookieFromDocument(cookieString = document.cookie): string | null {
  const prefix = `${CONSENT_COOKIE_NAME}=`;
  const part = cookieString
    .split(';')
    .map((segment) => segment.trim())
    .find((segment) => segment.startsWith(prefix));

  if (!part) return null;

  const encodedValue = part.slice(prefix.length);
  if (!encodedValue) return null;

  try {
    return decodeURIComponent(encodedValue);
  } catch {
    return null;
  }
}

export function writeConsentCookieToDocument(consent: ConsentRecord): void {
  if (typeof document === 'undefined') return;

  const secure = typeof window !== 'undefined' && window.location.protocol === 'https:';
  const parts = [
    `${CONSENT_COOKIE_NAME}=${encodeURIComponent(serializeConsentCookieValue(consent))}`,
    'Path=/',
    `Max-Age=${CONSENT_COOKIE_MAX_AGE_SECONDS}`,
    'SameSite=Lax',
  ];

  if (secure) {
    parts.push('Secure');
  }

  document.cookie = parts.join('; ');
}

function isValidConsentRecord(value: Partial<ConsentRecord>): value is ConsentRecord {
  if (value.version !== CONSENT_VERSION) return false;
  if (!isValidIsoDate(value.timestamp)) return false;
  if (typeof value.analytics !== 'boolean') return false;
  if (typeof value.session_recording !== 'boolean') return false;
  if (
    value.choice !== 'accept_all' &&
    value.choice !== 'reject_all' &&
    value.choice !== 'customise'
  ) {
    return false;
  }

  return true;
}

function isValidIsoDate(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return false;
  return parsed.toISOString() === value;
}
